import React from 'react';
import { Input, Button } from 'reactstrap';
import CurrencyInput from 'react-currency-input-field';
import Helpers from '../../../../../../utils/helper';

export type DetailRow = Record<string, string | null>;

const PAYMENT_NUMBER_COLUMN = 'Payment (#)';
const CALC_DV_COLUMN = 'Calculation Dependent Variable ("CalcDV")';
const TYPE_COLUMN = 'Type';
const TYPE_COLUMN_WIDTH = '105px';
const COLLAPSED_HEIGHT_PX = 30;
const EXPANDED_MAX_HEIGHT_PX = 500;
const ONE_LINE_TOLERANCE_PX = 10;

const expandTextarea = (el: HTMLTextAreaElement) => {
    el.style.height = '1px';
    const contentHeight = el.scrollHeight;
    const isOneLine =
        contentHeight <= COLLAPSED_HEIGHT_PX + ONE_LINE_TOLERANCE_PX;
    const heightPx = isOneLine
        ? COLLAPSED_HEIGHT_PX
        : Math.min(contentHeight, EXPANDED_MAX_HEIGHT_PX);
    el.style.height = `${heightPx}px`;
    el.style.overflow = contentHeight > EXPANDED_MAX_HEIGHT_PX ? 'auto' : 'hidden';
};

const collapseTextarea = (el: HTMLTextAreaElement) => {
    el.style.height = `${COLLAPSED_HEIGHT_PX}px`;
    el.style.overflow = 'hidden';
};

const validatePercentageField = (
    event: React.KeyboardEvent<HTMLInputElement>,
    keyProp: string
) => {
    const decimalLength = (value: string) =>
        value.includes('.') ? value.split('.').pop()?.length ?? 0 : 0;
    const selectionStart = event.currentTarget.selectionStart ?? 0;
    const isChangeAfterDot = selectionStart > keyProp.length - 2;
    const isThreeDigitsAfterDot = decimalLength(keyProp) >= 3;

    if (
        !/[0-9]/.test(event.key) &&
        event.key !== 'Backspace' &&
        event.key !== 'ArrowRight' &&
        event.key !== 'ArrowLeft' &&
        event.key !== 'Tab' &&
        (event.key !== '.' || keyProp.includes('.'))
    ) {
        return true;
    }

    if (event.key === '.' && !isChangeAfterDot) {
        return true;
    }

    if (/[0-9]/.test(event.key) && isThreeDigitsAfterDot && isChangeAfterDot) {
        return true;
    }

    return false;
};

export interface TermsFormDetailBlockProps {
    className?: string;
    title: string;
    columns: string[];
    currencyColumns?: string[];
    percentageColumns?: string[];
    rows: DetailRow[];
    readOnly: boolean;
    onRowsChange: (rows: DetailRow[]) => void;
    displayTotal?: number | null;
    showTotalRow?: boolean;
    onAddRow?: () => void;
    onRemoveRow?: (rowIndex: number) => void;
    singleRow?: boolean;
    hideAddRow?: boolean;
    selectOptions?: Record<string, string[]>;
    showPaymentNumber?: boolean;
    paymentNumberHeader?: string;
    showRowBorder?: boolean;
    hideLastRowBorder?: boolean;
    expandableTextInputs?: boolean;
    naWhenEmptyColumns?: string[];
    invalidCells?: Set<string>;
}

export const TermsFormDetailBlock = (props: TermsFormDetailBlockProps) => {
    const {
        className,
        title,
        columns,
        currencyColumns = [],
        percentageColumns = [],
        rows,
        readOnly,
        onRowsChange,
        displayTotal,
        showTotalRow,
        onAddRow,
        onRemoveRow,
        singleRow,
        hideAddRow,
        selectOptions,
        showPaymentNumber,
        paymentNumberHeader = PAYMENT_NUMBER_COLUMN,
        showRowBorder = true,
        hideLastRowBorder = false,
        expandableTextInputs = false,
        naWhenEmptyColumns = [],
        invalidCells,
    } = props;
    const currencyColumnSet = new Set(currencyColumns);
    const percentageColumnSet = new Set(percentageColumns);
    const naWhenEmptyColumnSet = new Set(naWhenEmptyColumns);

    const showRemoveRow = !readOnly && !singleRow && !!onRemoveRow;
    const colCount = columns.length + (showPaymentNumber ? 1 : 0) + (showRemoveRow ? 1 : 0);
    const gridTemplateColumns = [
        ...(showPaymentNumber ? ['minmax(0, 1fr)'] : []),
        ...columns.map((column) =>
            column === TYPE_COLUMN
                ? TYPE_COLUMN_WIDTH
                : column === CALC_DV_COLUMN
                  ? 'minmax(0, 1.6fr)'
                  : 'minmax(0, 1fr)'
        ),
        ...(showRemoveRow ? ['32px'] : []),
    ].join(' ');

    const displayRows: DetailRow[] =
        singleRow && rows.length === 0
            ? [columns.reduce<DetailRow>((acc, c) => ({ ...acc, [c]: null }), {})]
            : rows;

    const updateCell = (rowIndex: number, colKey: string, value: string | null) => {
        if (singleRow && rows.length === 0) {
            onRowsChange([{ ...displayRows[0], [colKey]: value }]);
            return;
        }
        const next = rows.map((r, i) =>
            i === rowIndex ? { ...r, [colKey]: value } : r
        );
        onRowsChange(next);
    };

    return (
        <div
            className={`terms-form-detail-block terms-form-table-layout${className ? ` ${className}` : ''}${rows.length > 0 ? ' terms-form-detail-block--has-rows' : ''}`}>
            <div className="terms-form-block-name">{title}</div>
            <div className="terms-form-table-scroll">
                <div
                    className="terms-form-table-grid"
                style={{ '--col-count': colCount, gridTemplateColumns } as React.CSSProperties}>
                <div className="terms-form-table-header">
                    {showPaymentNumber && (
                        <div className="terms-form-table-cell terms-form-table-header-cell">
                            {paymentNumberHeader}
                        </div>
                    )}
                    {columns.map((key) => (
                        <div
                            key={key}
                            className={`terms-form-table-cell terms-form-table-header-cell${key === TYPE_COLUMN ? ' terms-form-table-cell--type' : ''}`}>
                            {key}
                        </div>
                    ))}
                    {showRemoveRow && <div className="terms-form-table-cell terms-form-table-header-cell" />}
                </div>
                {displayRows.map((row, rowIndex) => (
                    <div key={rowIndex} className="terms-form-table-row">
                        {showPaymentNumber && (
                            <div className="terms-form-table-cell terms-form-table-cell-payment-num">
                                Payment {rowIndex + 1}
                            </div>
                        )}
                        {columns.map((key) => {
                            const val = row[key] ?? '';
                            const isCellInvalid = invalidCells?.has(`${rowIndex}:${key}`) ?? false;
                            const isPercentageColumn = percentageColumnSet.has(key);
                            const displayVal =
                                isPercentageColumn && val !== '' ? `${val}%` : val;
                            const displayReadOnlyVal =
                                naWhenEmptyColumnSet.has(key) && val === ''
                                    ? 'N/A'
                                    : displayVal;
                            return (
                                <div
                                    key={key}
                                    className={`terms-form-table-cell${key === TYPE_COLUMN ? ' terms-form-table-cell--type' : ''}`}>
                                    {readOnly ? (
                                        <Input
                                            type="text"
                                            value={displayReadOnlyVal}
                                            disabled
                                            className="form-control loi-form-input"
                                            aria-label={key}
                                        />
                                    ) : selectOptions?.[key] ? (
                                        <Input
                                            type="select"
                                            value={val}
                                            disabled={readOnly}
                                            onChange={(e) =>
                                                updateCell(
                                                    rowIndex,
                                                    key,
                                                    e.target.value === ''
                                                        ? null
                                                        : e.target.value
                                                )
                                            }
                                            invalid={isCellInvalid}
                                            className="form-control loi-form-input"
                                            aria-label={key}>
                                            <option value="" />
                                            {selectOptions[key].map((opt) => (
                                                <option key={opt} value={opt}>
                                                    {opt}
                                                </option>
                                            ))}
                                        </Input>
                                    ) : currencyColumnSet.has(key) ? (
                                        <CurrencyInput
                                            name={`${title}-${rowIndex}-${key}`}
                                            value={val}
                                            allowDecimals={true}
                                            allowNegativeValue={false}
                                            prefix="$"
                                            onValueChange={(value) =>
                                                updateCell(
                                                    rowIndex,
                                                    key,
                                                    value === undefined ||
                                                        value === '' ||
                                                        value === 'NaN'
                                                        ? null
                                                        : value
                                                )
                                            }
                                            disabled={readOnly}
                                            className={`form-control loi-form-input${isCellInvalid ? ' is-invalid' : ''}`}
                                            aria-label={key}
                                            placeholder={
                                                naWhenEmptyColumnSet.has(key) ? 'N/A' : undefined
                                            }
                                        />
                                    ) : isPercentageColumn ? (
                                        <Input
                                            type="text"
                                            value={displayVal}
                                            disabled={readOnly}
                                            onKeyDown={(event) => {
                                                if (validatePercentageField(event, val)) {
                                                    event.preventDefault();
                                                }
                                            }}
                                            onChange={(event) => {
                                                let value = event.target.value;
                                                if (
                                                    !event.target.value.includes('%') &&
                                                    Number(value) === Number(val)
                                                ) {
                                                    value = event.target.value.slice(0, -1);
                                                }
                                                const result = value.replace('%', '');
                                                updateCell(
                                                    rowIndex,
                                                    key,
                                                    result === '' ? null : result
                                                );
                                            }}
                                            invalid={isCellInvalid}
                                            className="form-control loi-form-input"
                                            aria-label={key}
                                        />
                                    ) : expandableTextInputs &&
                                      !currencyColumnSet.has(key) ? (
                                        <textarea
                                            className={`form-control loi-form-input terms-form-expandable-textarea${isCellInvalid ? ' is-invalid' : ''}`}
                                            style={{
                                                height: COLLAPSED_HEIGHT_PX,
                                                overflow: 'hidden',
                                                boxSizing: 'border-box',
                                                resize: 'none',
                                            }}
                                            value={val}
                                            onChange={(e) => {
                                                const el = e.target;
                                                updateCell(
                                                    rowIndex,
                                                    key,
                                                    el.value === '' ? null : el.value
                                                );
                                                expandTextarea(el);
                                            }}
                                            onFocus={(e) => expandTextarea(e.target)}
                                            onBlur={(e) => collapseTextarea(e.target)}
                                            aria-label={key}
                                        />
                                    ) : (
                                        <Input
                                            type="text"
                                            value={val}
                                            disabled={readOnly}
                                            onChange={(e) =>
                                                updateCell(
                                                    rowIndex,
                                                    key,
                                                    e.target.value === ''
                                                        ? null
                                                        : e.target.value
                                                )
                                            }
                                            invalid={isCellInvalid}
                                            className="form-control loi-form-input"
                                            aria-label={key}
                                        />
                                    )}
                                </div>
                            )
                        })}
                        {showRemoveRow && (
                            <div className="terms-form-table-cell terms-form-table-cell--remove">
                                <Button
                                    color="link"
                                    className="terms-form-remove-row-btn"
                                    onClick={() => onRemoveRow!(rowIndex)}
                                    aria-label="Remove row">
                                    ×
                                </Button>
                            </div>
                        )}
                        {showRowBorder &&
                            !(hideLastRowBorder && rowIndex === displayRows.length - 1) && (
                                <div
                                    className={`terms-form-table-row-border${rowIndex === 0 ? ' terms-form-table-row-border--first' : ''}`}
                                    style={{ gridColumn: '1 / -1' }}
                                />
                            )}
                    </div>
                ))}
            </div>
            </div>
            {showTotalRow && displayTotal != null && (
                <div className="terms-form-detail-block terms-form-summary-layout">
                    <div className="terms-form-form-content">
                        <div className="terms-form-summary-row terms-form-summary-total">
                            <div className="terms-form-col-item">Total</div>
                            <div className="terms-form-col-value">
                                <Input
                                    type="text"
                                    value={Helpers.currencyFormat(displayTotal, false)}
                                    disabled
                                    readOnly
                                    className="form-control loi-form-input"
                                    aria-label="Total"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {!readOnly && onAddRow && !singleRow && !hideAddRow && (
                <Button
                    color="link"
                    className="terms-form-add-payment-btn"
                    onClick={onAddRow}>
                    + Add another payment
                </Button>
            )}
        </div>
    );
};
